import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Badge, Modal, Spinner, Alert, Tabs, Tab } from 'react-bootstrap';
import { FaBalanceScale, FaCar, FaWallet, FaMoneyBillWave, FaUsers, FaBuilding, FaCalendarAlt, FaChartBar, FaPlus, FaSearch, FaEdit, FaTrash, FaUserPlus, FaUser, FaHandHoldingUsd, FaChartLine, FaFileAlt, FaChevronDown, FaChevronUp, FaDownload, FaInfo, FaSync } from 'react-icons/fa';
import { API_URL } from './config';
import 'bootstrap/dist/css/bootstrap.min.css';

const TellerControlPanel = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('rental-deposits');
    const [tellers, setTellers] = useState([]);
    const [selectedTeller, setSelectedTeller] = useState('');
    const [todayDate, setTodayDate] = useState(new Date().toISOString().slice(0, 19).replace('T', ' '));
    
    // Rental deposit state
    const [driverSearch, setDriverSearch] = useState('');
    const [searchedDrivers, setSearchedDrivers] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [rentalAmount, setRentalAmount] = useState('');
    const [rentalNotes, setRentalNotes] = useState('');
    
    // Wallet deposit state
    const [userSearch, setUserSearch] = useState('');
    const [searchedUsers, setSearchedUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [walletAmount, setWalletAmount] = useState('');
    const [walletNotes, setWalletNotes] = useState('');
    
    // Driver Withdrawable Funds state
    const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
    const [driverBalance, setDriverBalance] = useState(null);
    const [withdrawalMethod, setWithdrawalMethod] = useState('cash_pickup');
    const [withdrawalDetails, setWithdrawalDetails] = useState({
        bank_account_number: '',
        bank_name: '',
        account_holder_name: '',
        mobile_money_number: '',
        mobile_money_provider: ''
    });

    // Withdrawal state
    const [withdrawalDriverSearch, setWithdrawalDriverSearch] = useState('');
    const [withdrawalSearchedDrivers, setWithdrawalSearchedDrivers] = useState([]);
    const [selectedWithdrawalDriver, setSelectedWithdrawalDriver] = useState(null);
    const [withdrawalAmount, setWithdrawalAmount] = useState('');
    const [withdrawalNotes, setWithdrawalNotes] = useState('');
    
    // Add state for collapsible sections
    const [expandedSections, setExpandedSections] = useState({
        'help-tooltip': false,
        'recent-rental-transactions': true,
        'recent-wallet-transactions': true,
        'recent-withdrawals': true,
        'pending-withdrawals': true,
        'selected-user-transactions': true
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const exportData = (data, filename) => {
        const csv = [
            ['Time', 'Customer ID', 'Amount', 'Payment Method', 'Status', 'Notes'],
            ...data.map(item => [
                new Date(item.transaction_date).toLocaleString(),
                item.customer_id || item.driver_id || item.customerid || 'N/A',
                item.amount.toFixed(2),
                item.payment_method,
                item.transaction_status,
                item.notes || ''
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    };
    
    // Reports state
    const [dailySummary, setDailySummary] = useState(null);
    const [dailyTransactions, setDailyTransactions] = useState([]);
    const [reconciliationData, setReconciliationData] = useState(null);
    const [activeDrivers, setActiveDrivers] = useState([]);
    
    // Driver withdrawals state (separate from daily transactions)
    const [driverWithdrawals, setDriverWithdrawals] = useState([]);
    
    // Driver funds summary state
    const [driverFundsSummary, setDriverFundsSummary] = useState(null);
    
    // User wallet transactions state
    const [userWalletTransactions, setUserWalletTransactions] = useState([]);
    
    // UI state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    // Loading states for buttons
    const [isRecordingRentalDeposit, setIsRecordingRentalDeposit] = useState(false);
    const [isRecordingWalletDeposit, setIsRecordingWalletDeposit] = useState(false);
    const [isCreatingWithdrawalRequest, setIsCreatingWithdrawalRequest] = useState(false);
    const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState(false);
    const [quickProcessingWithdrawals, setQuickProcessingWithdrawals] = useState(new Set());

    useEffect(() => {
        fetchTellers();
        fetchDailySummary();
        fetchDailyTransactions();
        fetchPendingWithdrawals();
        fetchDriverWithdrawals(); // Fetch driver withdrawals separately
        fetchDriverFundsSummary(); // Fetch driver funds summary
        setLoading(false); // Set loading to false after initial fetch
    }, [todayDate]);

    const fetchTellers = async () => {
        try {
            const response = await fetch(`${API_URL}/teller/active-tellers`);
            const result = await response.json();
            if (result.success) {
                setTellers(result.tellers);
                if (result.tellers.length > 0 && !selectedTeller) {
                    setSelectedTeller(result.tellers[0].teller_id);
                }
            }
        } catch (error) {
            console.error('Error fetching tellers:', error);
            // Use sample data for demonstration
            const sampleTellers = [
                { teller_id: 'TELLER001', name: 'Sarah Johnson', email: 'sarah.johnson@xgo.com', phone: '+263 771 234 567', is_active: 1, hire_date: '2024-01-15' },
                { teller_id: 'TELLER002', name: 'Michael Chen', email: 'michael.chen@xgo.com', phone: '+263 772 345 678', is_active: 1, hire_date: '2024-01-20' },
                { teller_id: 'TELLER003', name: 'Grace Mbewe', email: 'grace.mbewe@xgo.com', phone: '+263 773 456 789', is_active: 1, hire_date: '2024-02-01' }
            ];
            setTellers(sampleTellers);
            if (!selectedTeller) {
                setSelectedTeller(sampleTellers[0].teller_id);
            }
        }
    };

    const fetchDailySummary = async () => {
        try {
            const response = await fetch(`${API_URL}/teller/daily-summary/${todayDate}`);
            const result = await response.json();
            if (result.success) {
                setDailySummary(result.summary);
            }
        } catch (error) {
            console.error('Error fetching daily summary:', error);
            // Use sample data for demonstration
            setDailySummary({
                total_transactions: 5,
                total_cash_in: 140.00,
                total_cash_out: 50.00,
                net_cash_movement: 90.00,
                rentalDeposits: 2,
                walletDeposits: 2,
                withdrawals: 1,
                totalDeposits: 140.00,
                totalWalletAmount: 50.00,
                totalWithdrawalAmount: 50.00
            });
        }
    };

    const fetchDailyTransactions = async () => {
        try {
            const response = await fetch(`${API_URL}/teller/daily-transactions/${todayDate}`);
            const result = await response.json();
            if (result.success) {
                setDailyTransactions(result.transactions);
            }
        } catch (error) {
            console.error('Error fetching daily transactions:', error);
            // Use sample data for demonstration
            setDailyTransactions([
                {
                    transaction_id: 1,
                    transaction_type: 'rental_deposit',
                    driver_id: 'DRV001',
                    user_id: null,
                    amount: 40.00,
                    payment_method: 'cash',
                    transaction_date: new Date().toISOString(),
                    teller_id: 'TELLER001',
                    transaction_reference: 'TXN001',
                    notes: 'Weekly rental payment',
                    status: 'completed'
                },
                {
                    transaction_id: 2,
                    transaction_type: 'wallet_deposit',
                    driver_id: null,
                    user_id: 'USR001',
                    amount: 25.00,
                    payment_method: 'cash',
                    transaction_date: new Date().toISOString(),
                    teller_id: 'TELLER001',
                    transaction_reference: 'TXN002',
                    notes: 'Wallet top-up',
                    status: 'completed'
                },
                {
                    transaction_id: 3,
                    transaction_type: 'withdrawal_disbursement',
                    driver_id: 'USR002',
                    user_id: null,
                    amount: 50.00,
                    payment_method: 'cash',
                    transaction_date: '2026-02-04 11:45:00',
                    teller_id: 'TELLER002',
                    transaction_reference: 'TXN003',
                    notes: 'Cash withdrawal',
                    status: 'completed',
                    customer_id: 'USR002'
                }
            ]);
        }
    };

    // Fetch recent driver withdrawals separately
    const fetchDriverWithdrawals = async () => {
        try {
            console.log('🔍 Fetching recent driver withdrawals...');
            const response = await fetch(`${API_URL}/driver-funds/recent-withdrawals?limit=10`);
            
            if (response.ok) {
                const data = await response.json();
                console.log('✅ Driver withdrawals fetched:', data);
                
                // Store driver withdrawals separately
                setDriverWithdrawals(data.transactions || []);
            } else {
                console.error('❌ Failed to fetch driver withdrawals');
                setDriverWithdrawals([]);
            }
        } catch (error) {
            console.error('❌ Error fetching driver withdrawals:', error);
            setDriverWithdrawals([]);
        }
    };

    const fetchDriverFundsSummary = async () => {
        try {
            console.log('🔍 Fetching driver funds summary...');
            const response = await fetch(`${API_URL}/driver-funds/summary`);
            
            if (response.ok) {
                const data = await response.json();
                console.log('✅ Driver funds summary fetched:', data);
                setDriverFundsSummary(data.data);
            } else {
                console.error('❌ Failed to fetch driver funds summary');
                setDriverFundsSummary(null);
            }
        } catch (error) {
            console.error('❌ Error fetching driver funds summary:', error);
            setDriverFundsSummary(null);
        }
    };

    const fetchUserWalletTransactions = async (userId) => {
        try {
            console.log('Updated endpoint - Fetching user wallet transactions for:', userId);
            const response = await fetch(`${API_URL}/topUp/topup/${userId}`);
            
            if (response.ok) {
                const data = await response.json();
                console.log('Updated endpoint - Fetching user wallet transactions for:', userId);
                setUserWalletTransactions(data || []);
            } else {
                console.error('Failed to fetch user wallet transactions');
                setUserWalletTransactions([]);
            }
        } catch (error) {
            console.error('Error fetching user wallet transactions:', error);
            setUserWalletTransactions([]);
        }
    };

    // Driver Withdrawable Funds API functions
    const fetchDriverBalance = async (driverId) => {
        try {
            console.log('🔍 Fetching driver balance for:', driverId);
            const response = await fetch(`${API_URL}/driver-funds/balance/${driverId}`);
            
            if (response.ok) {
                const data = await response.json();
                console.log('✅ Driver balance fetched:', data);
                setDriverBalance(data.data);
            } else {
                console.error('❌ Failed to fetch driver balance');
                setDriverBalance(null);
            }
        } catch (error) {
            console.error('❌ Error fetching driver balance:', error);
            setDriverBalance(null);
        }
    };

    const fetchPendingWithdrawals = async () => {
        try {
            console.log('🔍 Fetching pending withdrawals...');
            const response = await fetch(`${API_URL}/driver-funds/pending-withdrawals`);
            
            console.log('📡 Response status:', response.status);
            console.log('📡 Response headers:', response.headers);
            
            if (response.ok) {
                const data = await response.json();
                console.log('✅ Raw API response:', data);
                console.log('✅ Pending withdrawals data:', data.data);
                console.log('✅ Number of withdrawals:', data.data?.length || 0);
                
                setPendingWithdrawals(data.data || []);
                
                // Force a re-render by logging the state
                setTimeout(() => {
                    console.log('🔄 Pending withdrawals state updated:', data.data?.length || 0, 'items');
                }, 100);
            } else {
                console.error('❌ Failed to fetch pending withdrawals - Status:', response.status);
                const errorText = await response.text();
                console.error('❌ Error response:', errorText);
                setPendingWithdrawals([]);
            }
        } catch (error) {
            console.error('❌ Error fetching pending withdrawals:', error);
            setPendingWithdrawals([]);
        }
    };

    // Create withdrawal request only (without processing)
    const createWithdrawalRequest = async () => {
        if (!selectedWithdrawalDriver || !withdrawalAmount || !selectedTeller) {
            setErrorMessage('Please fill all required fields');
            setShowErrorModal(true);
            return;
        }

        // Validate withdrawal details based on method
        if (withdrawalMethod === 'bank_transfer') {
            if (!withdrawalDetails.bank_account_number || !withdrawalDetails.bank_name || !withdrawalDetails.account_holder_name) {
                setErrorMessage('Please fill all bank transfer details');
                setShowErrorModal(true);
                return;
            }
        } else if (withdrawalMethod === 'mobile_money') {
            if (!withdrawalDetails.mobile_money_number || !withdrawalDetails.mobile_money_provider) {
                setErrorMessage('Please fill all mobile money details');
                setShowErrorModal(true);
                return;
            }
        }

        setIsCreatingWithdrawalRequest(true);
        try {
            const withdrawalData = {
                client_profile_id: selectedWithdrawalDriver.driver_id,
                amount: parseFloat(withdrawalAmount),
                withdrawal_method: withdrawalMethod,
                withdrawal_details: withdrawalDetails
            };

            console.log('🔍 Creating withdrawal request:', withdrawalData);

            const response = await fetch(`${API_URL}/driver-funds/withdrawal-request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(withdrawalData)
            });

            const result = await response.json();
            console.log('🔍 Withdrawal request response:', result);

            if (result.status === 'success') {
                const driverName = selectedWithdrawalDriver ? `${selectedWithdrawalDriver.name} ${selectedWithdrawalDriver.surname}` : 'Driver';
                setSuccessMessage(`✅ Withdrawal request created successfully!\n\nDriver: ${driverName}\nAmount: $${withdrawalAmount}\nMethod: ${withdrawalMethod}\nRequest ID: ${result.data.request_id}\n\n📊 The request is now pending and can be processed below.`);
                setShowSuccessModal(true);
                
                // Reset form
                setSelectedWithdrawalDriver(null);
                setWithdrawalAmount('');
                setWithdrawalNotes('');
                setWithdrawalDriverSearch('');
                setWithdrawalSearchedDrivers([]);
                setWithdrawalMethod('cash_pickup');
                setWithdrawalDetails({
                    bank_account_number: '',
                    bank_name: '',
                    account_holder_name: '',
                    mobile_money_number: '',
                    mobile_money_provider: ''
                });
                setDriverBalance(null);
                
                // Refresh pending withdrawals
                await fetchPendingWithdrawals();
                await fetchDriverFundsSummary(); // Refresh driver funds summary
                
            } else {
                setErrorMessage(result.message || 'Failed to create withdrawal request');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error creating withdrawal request:', error);
            setErrorMessage('Failed to create withdrawal request');
            setShowErrorModal(true);
        } finally {
            setIsCreatingWithdrawalRequest(false);
        }
    };

    const processDriverWithdrawal = async () => {
        if (!selectedWithdrawalDriver || !withdrawalAmount || !selectedTeller) {
            setErrorMessage('Please fill all required fields');
            setShowErrorModal(true);
            return;
        }

        // Validate withdrawal details based on method
        if (withdrawalMethod === 'bank_transfer') {
            if (!withdrawalDetails.bank_account_number || !withdrawalDetails.bank_name || !withdrawalDetails.account_holder_name) {
                setErrorMessage('Please fill all bank transfer details');
                setShowErrorModal(true);
                return;
            }
        } else if (withdrawalMethod === 'mobile_money') {
            if (!withdrawalDetails.mobile_money_number || !withdrawalDetails.mobile_money_provider) {
                setErrorMessage('Please fill all mobile money details');
                setShowErrorModal(true);
                return;
            }
        }

        setIsProcessingWithdrawal(true);
        try {
            const withdrawalData = {
                client_profile_id: selectedWithdrawalDriver.driver_id,
                amount: parseFloat(withdrawalAmount),
                withdrawal_method: withdrawalMethod,
                withdrawal_details: withdrawalDetails
            };

            console.log('🔍 Processing driver withdrawal:', withdrawalData);

            // First create withdrawal request
            const response = await fetch(`${API_URL}/driver-funds/withdrawal-request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(withdrawalData)
            });

            const result = await response.json();
            console.log('🔍 Withdrawal request response:', result);

            if (result.status === 'success') {
                // Then process the withdrawal (admin approval)
                const processResponse = await fetch(`${API_URL}/driver-funds/process-withdrawal`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        request_id: result.data.request_id,
                        status: 'approved',
                        processed_by: selectedTeller
                    })
                });

                const processResult = await processResponse.json();
                console.log('🔍 Withdrawal process response:', processResult);

                if (processResult.status === 'success') {
                    const driverName = selectedWithdrawalDriver ? `${selectedWithdrawalDriver.name} ${selectedWithdrawalDriver.surname}` : 'Driver';
                    setSuccessMessage(`✅ Driver earnings withdrawal of $${withdrawalAmount} processed for ${driverName}. Method: ${withdrawalMethod}\n\n📊 Data has been refreshed - pending withdrawals updated!`);
                    setShowSuccessModal(true);
                    
                    // Reset form
                    setSelectedWithdrawalDriver(null);
                    setWithdrawalAmount('');
                    setWithdrawalNotes('');
                    setWithdrawalDriverSearch('');
                    setWithdrawalSearchedDrivers([]);
                    setWithdrawalMethod('cash_pickup');
                    setWithdrawalDetails({
                        bank_account_number: '',
                        bank_name: '',
                        account_holder_name: '',
                        mobile_money_number: '',
                        mobile_money_provider: ''
                    });
                    setDriverBalance(null);
                    
                    // Refresh all data after successful withdrawal
                    console.log('🔄 Refreshing data after successful withdrawal...');
                    await fetchPendingWithdrawals();
                    
                    // Also refresh daily transactions to show the new withdrawal
                    await fetchDailyTransactions();
                    
                    // Show success message with data refresh confirmation
                    setTimeout(() => {
                        console.log('✅ Data refresh completed - pending withdrawals updated');
                    }, 500);
                    
                } else {
                    setErrorMessage(processResult.message || 'Failed to process withdrawal');
                    setShowErrorModal(true);
                }
            } else {
                setErrorMessage(result.message || 'Failed to create withdrawal request');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error processing driver withdrawal:', error);
            setErrorMessage('Failed to process withdrawal');
            setShowErrorModal(true);
        } finally {
            setIsProcessingWithdrawal(false);
        }
    };

    // Quick process withdrawal from pending table
    const quickProcessWithdrawal = async (withdrawal) => {
        if (!selectedTeller) {
            setErrorMessage('Please select a teller first');
            setShowErrorModal(true);
            return;
        }

        // Add this withdrawal to the processing set
        setQuickProcessingWithdrawals(prev => new Set(prev).add(withdrawal.request_id));
        
        try {
            console.log('🚀 Quick processing withdrawal:', withdrawal);
            
            // Process the withdrawal directly
            const processResponse = await fetch(`${API_URL}/driver-funds/process-withdrawal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    request_id: withdrawal.request_id,
                    status: 'approved',
                    processed_by: selectedTeller
                })
            });

            const processResult = await processResponse.json();
            console.log('🔍 Quick process response:', processResult);

            if (processResult.status === 'success') {
                setSuccessMessage(`✅ Quick withdrawal processed successfully!\n\nDriver: ${withdrawal.client_profile_id}\nAmount: $${withdrawal.amount.toFixed(2)}\nMethod: ${withdrawal.withdrawal_method.replace('_', ' ')}\n\n📊 Data has been refreshed!`);
                setShowSuccessModal(true);
                
                // Refresh data
                await fetchPendingWithdrawals();
                await fetchDailyTransactions();
                await fetchDriverFundsSummary(); // Refresh driver funds summary
                
                setTimeout(() => {
                    console.log('✅ Quick process data refresh completed');
                }, 500);
                
            } else {
                setErrorMessage(processResult.message || 'Failed to process withdrawal');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error quick processing withdrawal:', error);
            setErrorMessage('Failed to process withdrawal');
            setShowErrorModal(true);
        } finally {
            // Remove this withdrawal from the processing set
            setQuickProcessingWithdrawals(prev => {
                const newSet = new Set(prev);
                newSet.delete(withdrawal.request_id);
                return newSet;
            });
        }
    };

    const handleUserSelection = async (user) => {
        setSelectedUser(user);
        setUserSearch(`${user.name} ${user.surname} (${user.id})`);
        setSearchedUsers([]);
        // Fetch user's recent wallet transactions when they are selected
        if (user && user.id) {
            await fetchUserWalletTransactions(user.id);
        }
    };

    const handleWithdrawalDriverSelection = async (driver) => {
        setSelectedWithdrawalDriver(driver);
        setWithdrawalDriverSearch(`${driver.name} ${driver.surname} (${driver.driver_id})`);
        setWithdrawalSearchedDrivers([]);
        // Fetch driver's balance when they are selected
        if (driver && driver.driver_id) {
            await fetchDriverBalance(driver.driver_id);
        }
    };

    const searchDrivers = async (searchTerm) => {
        console.log('🔍 searchDrivers called with:', searchTerm);
        if (searchTerm.length < 2) {
            console.log('📝 Search term too short, clearing results');
            setSearchedDrivers([]);
            return;
        }
        
        try {
            // Try API first, fall back to mock data
            console.log('🌐 Trying API endpoint...');
            const response = await fetch(`${API_URL}/teller/search-drivers/${searchTerm}`);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const result = await response.json();
            if (result.success) {
                console.log('✅ API success, setting drivers:', result.drivers);
                setSearchedDrivers(result.drivers);
            }
        } catch (error) {
            console.log('❌ API failed with error:', error);
            console.log('❌ API failed, using mock driver data for search');
            // Mock driver data for demonstration
            const mockDrivers = [
                {
                    driver_id: 'DRV001',
                    name: 'John',
                    surname: 'Smith',
                    phone: '+1234567890',
                    plate: 'ABC-123'
                },
                {
                    driver_id: 'DRV002', 
                    name: 'Jane',
                    surname: 'Doe',
                    phone: '+0987654321',
                    plate: 'XYZ-789'
                },
                {
                    driver_id: 'DRV003',
                    name: 'Mike',
                    surname: 'Johnson',
                    phone: '+1122334455',
                    plate: 'DEF-456'
                }
            ];
            
            const filtered = mockDrivers.filter(driver => 
                driver.driver_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.phone.includes(searchTerm) ||
                (driver.plate && driver.plate.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            
            console.log('🔍 Mock search results:', filtered);
            setSearchedDrivers(filtered);
        }
    };

    const searchWithdrawalDrivers = async (searchTerm) => {
        console.log('🔍 searchWithdrawalDrivers called with:', searchTerm);
        console.log('🌐 API_URL:', API_URL);
        
        if (searchTerm.length < 2) {
            console.log('📝 Search term too short, clearing results');
            setWithdrawalSearchedDrivers([]);
            return;
        }
        
        try {
            // Try API first, fall back to mock data
            console.log('🌐 Trying API endpoint:', `${API_URL}/teller/search-drivers/${searchTerm}`);
            const response = await fetch(`${API_URL}/teller/search-drivers/${searchTerm}`);
            
            console.log('📡 Response status:', response.status);
            console.log('📡 Response ok:', response.ok);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const result = await response.json();
            console.log('📊 API response:', result);
            
            if (result.success) {
                console.log('✅ API success, setting withdrawal drivers:', result.drivers);
                setWithdrawalSearchedDrivers(result.drivers);
            } else {
                console.log('❌ API returned success:false:', result);
                setWithdrawalSearchedDrivers([]);
            }
        } catch (error) {
            console.log('❌ API failed with error:', error);
            console.log('❌ API failed, using mock driver data for withdrawal search');
            // Mock driver data for demonstration
            const mockDrivers = [
                {
                    driver_id: 'DRV001',
                    name: 'John',
                    surname: 'Smith',
                    phone: '+1234567890',
                    email: 'john.smith@example.com',
                    plate: 'ABC-123'
                },
                {
                    driver_id: 'DRV002',
                    name: 'Jane',
                    surname: 'Doe',
                    phone: '+0987654321',
                    email: 'jane.doe@example.com',
                    plate: 'XYZ-789'
                },
                {
                    driver_id: 'DRV003',
                    name: 'Mike',
                    surname: 'Johnson',
                    phone: '+1122334455',
                    email: 'mike.johnson@example.com',
                    plate: 'DEF-456'
                },
                {
                    driver_id: 'DRV004',
                    name: 'Sarah',
                    surname: 'Williams',
                    phone: '+5544332211',
                    email: 'sarah.williams@example.com',
                    plate: 'GHI-789'
                }
            ];
            
            const filtered = mockDrivers.filter(driver => 
                driver.driver_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.phone.includes(searchTerm) ||
                (driver.plate && driver.plate.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            
            console.log('🔍 Mock withdrawal search results:', filtered);
            setWithdrawalSearchedDrivers(filtered);
        }
    };

    const searchUsers = async (searchTerm) => {
        console.log('🔍 searchUsers called with:', searchTerm);
        if (searchTerm.length < 2) {
            console.log('📝 Search term too short, clearing results');
            setSearchedUsers([]);
            return;
        }
        
        try {
            // Try API first, fall back to mock data
            console.log('🌐 Trying API endpoint...');
            const response = await fetch(`${API_URL}/teller/search-users/${searchTerm}`);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const result = await response.json();
            if (result.success) {
                console.log('✅ API success, setting users:', result.users);
                setSearchedUsers(result.users);
            }
        } catch (error) {
            console.log('❌ API failed with error:', error);
            console.log('❌ API failed, using mock user data for search');
            // Mock user data for demonstration (both customers and drivers for wallet deposits)
            const mockUsers = [
                {
                    id: 'CUST001',
                    name: 'Alice',
                    surname: 'Wilson',
                    phone: '+1234567890',
                    email: 'alice@example.com',
                    type: 'customer'
                },
                {
                    id: 'CUST002',
                    name: 'Bob',
                    surname: 'Brown',
                    phone: '+0987654321', 
                    email: 'bob@example.com',
                    type: 'customer'
                },
                {
                    id: 'DRV001',
                    name: 'John',
                    surname: 'Smith',
                    phone: '+1234567890',
                    email: 'john.driver@example.com',
                    type: 'driver'
                },
                {
                    id: 'DRV002',
                    name: 'Jane',
                    surname: 'Doe',
                    phone: '+0987654321',
                    email: 'jane.driver@example.com',
                    type: 'driver'
                }
            ];
            
            const filtered = mockUsers.filter(user => 
                user.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.phone.includes(searchTerm) ||
                (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            
            console.log('🔍 Mock search results:', filtered);
            setSearchedUsers(filtered);
        }
    };

    const recordRentalDeposit = async () => {
        if (!selectedDriver || !rentalAmount || !selectedTeller) {
            setErrorMessage('Please fill all required fields');
            setShowErrorModal(true);
            return;
        }

        setIsRecordingRentalDeposit(true);
        try {
            const response = await fetch(`${API_URL}/teller/record-rental-deposit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    driver_id: selectedDriver.driver_id,
                    deposit_amount: parseFloat(rentalAmount),
                    deposit_date: todayDate,
                    payment_method: 'cash',
                    teller_id: selectedTeller,
                    notes: rentalNotes
                })
            });

            const result = await response.json();
            if (result.success) {
                const driverName = selectedDriver ? `${selectedDriver.name} ${selectedDriver.surname}` : 'Driver';
                setSuccessMessage(`Rental deposit of $${rentalAmount} recorded for ${driverName}`);
                setShowSuccessModal(true);
                
                // Reset form
                setSelectedDriver(null);
                setRentalAmount('');
                setRentalNotes('');
                setDriverSearch('');
                setSearchedDrivers([]);
                
                // Refresh data
                fetchDailySummary();
                fetchDailyTransactions();
            } else {
                setErrorMessage(result.message || 'Failed to record rental deposit');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error recording rental deposit:', error);
            setErrorMessage('Failed to record rental deposit');
            setShowErrorModal(true);
        } finally {
            setIsRecordingRentalDeposit(false);
        }
    };

    const recordWalletDeposit = async () => {
        if (!selectedUser || !walletAmount || !selectedTeller) {
            setErrorMessage('Please fill all required fields');
            setShowErrorModal(true);
            return;
        }

        setIsRecordingWalletDeposit(true);
        try {
            const payload = {
                user_id: selectedUser.id,
                deposit_amount: parseFloat(walletAmount),
                deposit_date: todayDate,
                payment_method: 'cash',
                teller_id: selectedTeller,
                notes: walletNotes
            };
            
            console.log('🔍 Sending wallet deposit payload:', payload);
            
            const response = await fetch(`${API_URL}/teller/record-wallet-deposit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            
            if (result.success) {
                const userName = selectedUser ? `${selectedUser.name} ${selectedUser.surname}` : 'User';
                const userType = selectedUser ? selectedUser.type : 'User';
                
                let successMessage = '';
                if (userType === 'customer') {
                    successMessage = `User wallet funded with $${walletAmount} for Customer: ${userName} (for ride payments)`;
                } else {
                    successMessage = `User wallet funded with $${walletAmount} for Driver: ${userName} (for commission deduction)`;
                }
                
                setSuccessMessage(successMessage);
                setShowSuccessModal(true);
                
                // Fetch user's recent wallet transactions
                if (selectedUser && selectedUser.id) {
                    await fetchUserWalletTransactions(selectedUser.id);
                }
                
                // Reset form
                setSelectedUser(null);
                setWalletAmount('');
                setWalletNotes('');
                setUserSearch('');
                setSearchedUsers([]);
                
                // Refresh data
                fetchDailySummary();
                fetchDailyTransactions();
            } else {
                setErrorMessage(result.message || 'Failed to record wallet deposit');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error recording wallet deposit:', error);
            setErrorMessage('Failed to record wallet deposit');
            setShowErrorModal(true);
        } finally {
            setIsRecordingWalletDeposit(false);
        }
    };

    
    const renderRentalDepositsTab = () => (
        <div className="row">
            {/* Form Section */}
            <div className="col-lg-5">
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-primary text-white">
                        <div className="d-flex align-items-center">
                            <FaCar className="me-2" />
                            <Card.Title className="mb-0">Rental Fee Deposit</Card.Title>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaUsers className="me-1 text-primary" />
                                    Select Teller
                                </Form.Label>
                                <Form.Select value={selectedTeller} onChange={(e) => setSelectedTeller(e.target.value)}>
                                    <option value="">Choose Teller...</option>
                                    {tellers.map(teller => (
                                        <option key={teller.teller_id} value={teller.teller_id}>
                                            {teller.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaSearch className="me-1 text-primary" />
                                    Search Driver
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter driver ID, name, phone, or plate..."
                                    value={driverSearch}
                                    onChange={(e) => {
                                        console.log('🔄 Driver search input changed:', e.target.value);
                                        setDriverSearch(e.target.value);
                                        searchDrivers(e.target.value);
                                    }}
                                />
                                {searchedDrivers.length > 0 && (
                                    <div className="border rounded mt-2 max-h-40 overflow-auto bg-white shadow-sm">
                                        {searchedDrivers.map(driver => (
                                            <div
                                                key={driver.driver_id}
                                                className="p-3 border-bottom cursor-pointer hover:bg-light"
                                                onClick={() => {
                                                    setSelectedDriver(driver);
                                                    setDriverSearch(`${driver.name} ${driver.surname} (${driver.driver_id})`);
                                                    setSearchedDrivers([]);
                                                }}
                                            >
                                                <div className="fw-medium">
                                                    {driver.name} {driver.surname}
                                                </div>
                                                <small className="text-muted">
                                                    ID: {driver.driver_id} | Phone: {driver.phone}
                                                </small>
                                                {driver.plate && <div className="text-muted">Plate: {driver.plate}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>

                            {selectedDriver && (
                                <Alert variant="info" className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <FaUser className="me-2" />
                                        <div>
                                            <strong>Selected Driver:</strong><br />
                                            {selectedDriver.name} {selectedDriver.surname}
                                        </div>
                                    </div>
                                </Alert>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaMoneyBillWave className="me-1 text-success" />
                                    Deposit Amount ($)
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={rentalAmount}
                                    onChange={(e) => setRentalAmount(e.target.value)}
                                    size="lg"
                                />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label className="fw-semibold">
                                    <FaFileAlt className="me-1 text-secondary" />
                                    Notes (Optional)
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Add any notes about this deposit..."
                                    value={rentalNotes}
                                    onChange={(e) => setRentalNotes(e.target.value)}
                                />
                            </Form.Group>

                            <Button 
                                variant="primary" 
                                size="lg" 
                                className="w-100"
                                onClick={recordRentalDeposit}
                                disabled={!selectedDriver || !rentalAmount || !selectedTeller || isRecordingRentalDeposit}
                            >
                                {isRecordingRentalDeposit ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        Recording Deposit...
                                    </>
                                ) : (
                                    <>
                                        <FaPlus className="me-2" />
                                        Record Rental Deposit
                                    </>
                                )}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </div>

            {/* Summary Section */}
            <div className="col-lg-7">
                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <FaWallet className="text-primary mb-2" size={24} />
                                <h6 className="text-muted mb-1">Today's Total</h6>
                                <h4 className="text-primary mb-0">${dailySummary?.totalDeposits || '0.00'}</h4>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <FaCar className="text-success mb-2" size={24} />
                                <h6 className="text-muted mb-1">Rental Deposits</h6>
                                <h4 className="text-success mb-0">{dailySummary?.rentalDeposits || 0}</h4>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <FaUsers className="text-info mb-2" size={24} />
                                <h6 className="text-muted mb-1">Active Drivers</h6>
                                <h4 className="text-info mb-0">{activeDrivers.length}</h4>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Recent Transactions */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                    <div 
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-pointer"
                        onClick={() => toggleSection('recent-rental-transactions')}
                    >
                        <div className="flex items-center space-x-3">
                            <FaChartBar size={20} />
                            <h3 className="text-lg font-semibold">Recent Rental Transactions</h3>
                            <span className="bg-white/20 px-2 py-1 rounded text-sm">
                                {dailyTransactions.filter(t => t.transaction_type === 'rental_deposit').length} records
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    exportData(
                                        dailyTransactions.filter(t => t.transaction_type === 'rental_deposit'),
                                        'rental_transactions.csv'
                                    );
                                }}
                                className="p-2 hover:bg-white/20 rounded transition-colors"
                            >
                                <FaDownload size={16} />
                            </button>
                            {expandedSections['recent-rental-transactions'] ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                        </div>
                    </div>
                    
                    {expandedSections['recent-rental-transactions'] && (
                        <div className="p-4">
                            <div className="table-responsive" style={{maxHeight: '400px'}}>
                                <table className="table table-hover mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>Time</th>
                                            <th>Driver</th>
                                            <th>Amount</th>
                                            <th>Method</th>
                                            <th>Status</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dailyTransactions
                                            .filter(t => t.transaction_type === 'rental_deposit')
                                            .slice(0, 10)
                                            .map(transaction => (
                                                <tr key={transaction.transaction_id}>
                                                    <td>
                                                        <small className="text-muted">
                                                            {new Date(transaction.transaction_date).toLocaleTimeString()}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <div className="fw-medium">{transaction.customer_id}</div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold text-success">${transaction.amount.toFixed(2)}</span>
                                                    </td>
                                                    <td>
                                                        <Badge variant="secondary" size="sm">
                                                            {transaction.payment_method}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge variant="success" size="sm">
                                                            {transaction.transaction_status}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {transaction.notes || '-'}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const generateDayEndReport = async () => {
        try {
            const response = await fetch(`${API_URL}/teller/day-end-reconciliation/${todayDate}`);
            const result = await response.json();
            if (result.success) {
                setReconciliationData(result.reconciliation);
                setActiveTab('reports');
            }
        } catch (error) {
            console.error('Error generating day end report:', error);
            setErrorMessage('Failed to generate day end report');
            setShowErrorModal(true);
        }
    };

    const renderWalletDepositsTab = () => (
        <div className="row">
            {/* Form Section */}
            <div className="col-lg-5">
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-success text-white">
                        <div className="d-flex align-items-center">
                            <FaWallet className="me-2" />
                            <Card.Title className="mb-0">Fund User Wallet</Card.Title>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaUsers className="me-1 text-success" />
                                    Select Teller
                                </Form.Label>
                                <Form.Select value={selectedTeller} onChange={(e) => setSelectedTeller(e.target.value)}>
                                    <option value="">Choose Teller...</option>
                                    {tellers.map(teller => (
                                        <option key={teller.teller_id} value={teller.teller_id}>
                                            {teller.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaSearch className="me-1 text-success" />
                                    Search Customer or Driver
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter ID, name, email, or phone..."
                                    value={userSearch}
                                    onChange={(e) => {
                                        console.log('🔄 User search input changed:', e.target.value);
                                        setUserSearch(e.target.value);
                                        searchUsers(e.target.value);
                                    }}
                                />
                                {searchedUsers.length > 0 && (
                                    <div className="border rounded mt-2 max-h-40 overflow-auto bg-white shadow-sm">
                                        {searchedUsers.map(user => (
                                            <div
                                                key={user.id}
                                                className="p-3 border-bottom cursor-pointer hover:bg-light"
                                                onClick={() => handleUserSelection(user)}
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="fw-medium">
                                                        {user.name} {user.surname}
                                                    </div>
                                                    <Badge 
                                                        variant={user.type === 'customer' ? 'primary' : 'success'} 
                                                        size="sm"
                                                    >
                                                        {user.type === 'customer' ? 'Customer' : 'Driver'}
                                                    </Badge>
                                                </div>
                                                <small className="text-muted">
                                                    ID: {user.id} | Phone: {user.phone}
                                                </small>
                                                {user.email && <div className="text-muted">{user.email}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>

                            {selectedUser && (
                                <Alert variant="success" className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <FaUserPlus className="me-2" />
                                        <div>
                                            <strong>Selected {selectedUser.type === 'customer' ? 'Customer' : 'Driver'}:</strong><br />
                                            {selectedUser.name} {selectedUser.surname} ({selectedUser.id})
                                        </div>
                                    </div>
                                </Alert>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaHandHoldingUsd className="me-1 text-success" />
                                    User Wallet Amount ($)
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={walletAmount}
                                    onChange={(e) => setWalletAmount(e.target.value)}
                                    size="lg"
                                />
                                <Form.Text className="text-muted">
                                    Funds user wallet - usage depends on account type
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label className="fw-semibold">
                                    <FaFileAlt className="me-1 text-secondary" />
                                    Notes (Optional)
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Add any notes about this deposit..."
                                    value={walletNotes}
                                    onChange={(e) => setWalletNotes(e.target.value)}
                                />
                            </Form.Group>

                            <Button 
                                variant="success" 
                                size="lg" 
                                className="w-100"
                                onClick={recordWalletDeposit}
                                disabled={!selectedUser || !walletAmount || !selectedTeller || isRecordingWalletDeposit}
                            >
                                {isRecordingWalletDeposit ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        Funding Wallet...
                                    </>
                                ) : (
                                    <>
                                        <FaUserPlus className="me-2" />
                                        Fund User Wallet
                                    </>
                                )}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </div>

            {/* Selected User Wallet Transactions Section */}
            {selectedUser && (
                <div className="col-lg-7 mt-4">
                    {/* Summary Cards */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <Card className="text-center border-0 shadow-sm">
                                <Card.Body>
                                    <FaWallet className="text-success mb-2" size={24} />
                                    <h6 className="text-muted mb-1">User Wallets Funded</h6>
                                    <h4 className="text-success mb-0">{dailyTransactions.filter(t => t.transaction_type === 'wallet_deposit').length}</h4>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-md-4">
                            <Card className="text-center border-0 shadow-sm">
                                <Card.Body>
                                    <FaMoneyBillWave className="text-primary mb-2" size={24} />
                                    <h6 className="text-muted mb-1">Total Amount</h6>
                                    <h4 className="text-primary mb-0">${dailyTransactions.filter(t => t.transaction_type === 'wallet_deposit').reduce((sum, t) => sum + t.amount, 0).toFixed(2)}</h4>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-md-4">
                            <Card className="text-center border-0 shadow-sm">
                                <Card.Body>
                                    <FaUsers className="text-info mb-2" size={24} />
                                    <h6 className="text-muted mb-1">Users Funded</h6>
                                    <h4 className="text-info mb-0">{[...new Set(dailyTransactions.filter(t => t.transaction_type === 'wallet_deposit').map(t => t.user_id))].length}</h4>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                    {/* User Transactions Table */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                        <div 
                            className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-pointer"
                            onClick={() => toggleSection('selected-user-transactions')}
                        >
                            <div className="flex items-center space-x-3">
                                <FaWallet size={18} />
                                <h3 className="text-base font-semibold mb-0">
                                    Recent User Wallet Transactions
                                </h3>
                                <span className="bg-white/20 px-2 py-1 rounded text-xs">
                                    {userWalletTransactions.length} records
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        exportData(
                                            userWalletTransactions.map(t => ({
                                                transaction_date: t.date,
                                                customer_id: selectedUser.id,
                                                amount: parseFloat(t.user_wallet_credit || t.user_wallet_debit || 0),
                                                payment_method: 'cash',
                                                transaction_status: 'completed',
                                                notes: t.notes
                                            })),
                                            `${selectedUser.id}_wallet_transactions.csv`
                                        );
                                    }}
                                    className="p-1 hover:bg-white/20 rounded transition-colors"
                                >
                                    <FaDownload size={14} />
                                </button>
                                {expandedSections['selected-user-transactions'] ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}
                            </div>
                        </div>
                        
                        {expandedSections['selected-user-transactions'] && (
                            <div className="p-4">
                                {userWalletTransactions.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-striped table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Time</th>
                                                    <th>User</th>
                                                    <th>Amount</th>
                                                    <th>Balance</th>
                                                    <th>Method</th>
                                                    <th>Status</th>
                                                    <th>Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userWalletTransactions.map((transaction, index) => (
                                                    <tr key={transaction.top_up_id || index}>
                                                        <td>
                                                            {new Date(transaction.date).toLocaleString()}
                                                        </td>
                                                        <td>{selectedUser.id}</td>
                                                        <td className={transaction.user_wallet_credit > 0 ? 'text-success' : 'text-danger'}>
                                                            {transaction.user_wallet_credit > 0 ? 
                                                                `+$${parseFloat(transaction.user_wallet_credit).toFixed(2)}` : 
                                                                `-$${parseFloat(transaction.user_wallet_debit).toFixed(2)}`
                                                            }
                                                        </td>
                                                        <td className="fw-bold text-primary">
                                                            ${parseFloat(transaction.user_wallet_balance || 0).toFixed(2)}
                                                        </td>
                                                        <td>cash</td>
                                                        <td>
                                                            <Badge variant="success" size="sm">completed</Badge>
                                                        </td>
                                                        <td>{transaction.description || transaction.notes || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <FaWallet className="text-muted mb-3" size={48} />
                                        <p className="text-muted">No wallet transactions found for this user.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Summary Section - Only show when no user is selected */}
            {!selectedUser && (
                <div className="col-lg-7">
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <Card className="text-center border-0 shadow-sm">
                                <Card.Body>
                                    <FaWallet className="text-success mb-2" size={24} />
                                    <h6 className="text-muted mb-1">User Wallets Funded</h6>
                                    <h4 className="text-success mb-0">{dailyTransactions.filter(t => t.transaction_type === 'wallet_deposit').length}</h4>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-md-4">
                            <Card className="text-center border-0 shadow-sm">
                                <Card.Body>
                                    <FaMoneyBillWave className="text-primary mb-2" size={24} />
                                    <h6 className="text-muted mb-1">Total Amount</h6>
                                    <h4 className="text-primary mb-0">${dailyTransactions.filter(t => t.transaction_type === 'wallet_deposit').reduce((sum, t) => sum + t.amount, 0).toFixed(2)}</h4>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-md-4">
                            <Card className="text-center border-0 shadow-sm">
                                <Card.Body>
                                    <FaUsers className="text-info mb-2" size={24} />
                                    <h6 className="text-muted mb-1">Users Funded</h6>
                                    <h4 className="text-info mb-0">{[...new Set(dailyTransactions.filter(t => t.transaction_type === 'wallet_deposit').map(t => t.user_id))].length}</h4>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                {/* Recent User Wallet Transactions */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                    <div 
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-green-500 to-green-600 text-white cursor-pointer"
                        onClick={() => toggleSection('recent-wallet-transactions')}
                    >
                        <div className="flex items-center space-x-3">
                            <FaChartLine size={20} />
                            <h3 className="text-lg font-semibold">Recent User Wallet Transactions</h3>
                            <span className="bg-white/20 px-2 py-1 rounded text-sm">
                                {dailyTransactions.filter(t => t.transaction_type === 'wallet_deposit').length} records
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    exportData(
                                        dailyTransactions.filter(t => t.transaction_type === 'wallet_deposit'),
                                        'user_wallet_transactions.csv'
                                    );
                                }}
                                className="p-2 hover:bg-white/20 rounded transition-colors"
                            >
                                <FaDownload size={16} />
                            </button>
                            {expandedSections['recent-wallet-transactions'] ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                        </div>
                    </div>
                    
                    {expandedSections['recent-wallet-transactions'] && (
                        <div className="p-4">
                            <div className="table-responsive" style={{maxHeight: '400px'}}>
                                <table className="table table-hover mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>Time</th>
                                            <th>User</th>
                                            <th>Amount</th>
                                            <th>Method</th>
                                            <th>Status</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dailyTransactions
                                            .filter(t => t.transaction_type === 'wallet_deposit')
                                            .slice(0, 10)
                                            .map(transaction => (
                                                <tr key={transaction.transaction_id}>
                                                    <td>
                                                        <small className="text-muted">
                                                            {new Date(transaction.transaction_date).toLocaleTimeString()}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <div className="fw-medium">{transaction.customer_id}</div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold text-success">${transaction.amount.toFixed(2)}</span>
                                                    </td>
                                                    <td>
                                                        <Badge variant="secondary" size="sm">
                                                            {transaction.payment_method}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge variant="success" size="sm">
                                                            {transaction.transaction_status}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {transaction.notes || '-'}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
                </div>
            )}
        </div>
    );

    const renderWithdrawalsTab = () => (
        <div className="row">
            {/* Form Section */}
            <div className="col-lg-5">
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-primary text-white">
                        <h4 className="mb-0">
                            <FaPlus className="me-2" />
                            Create New Withdrawal Request
                        </h4>
                    </Card.Header>
                    <Card.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaUsers className="me-1 text-warning" />
                                    Select Teller
                                </Form.Label>
                                <Form.Select value={selectedTeller} onChange={(e) => setSelectedTeller(e.target.value)}>
                                    <option value="">Choose Teller...</option>
                                    {tellers.map(teller => (
                                        <option key={teller.teller_id} value={teller.teller_id}>
                                            {teller.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaSearch className="me-1 text-warning" />
                                    Search Driver
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter driver ID, name, phone, or plate..."
                                    value={withdrawalDriverSearch}
                                    onChange={(e) => {
                                        console.log('🔄 Withdrawal driver search input changed:', e.target.value);
                                        setWithdrawalDriverSearch(e.target.value);
                                        searchWithdrawalDrivers(e.target.value);
                                    }}
                                />
                                {withdrawalSearchedDrivers.length > 0 && (
                                    <>
                                        {console.log('🎯 Displaying withdrawal search results:', withdrawalSearchedDrivers.length, 'drivers')}
                                        <div className="border rounded mt-2 max-h-40 overflow-auto bg-white shadow-sm">
                                        {withdrawalSearchedDrivers.map(driver => (
                                            <div
                                                key={driver.driver_id}
                                                className="p-3 border-bottom cursor-pointer hover:bg-light"
                                                onClick={() => {
                                                    handleWithdrawalDriverSelection(driver);
                                                    setWithdrawalDriverSearch(`${driver.name} ${driver.surname} (${driver.driver_id})`);
                                                    setWithdrawalSearchedDrivers([]);
                                                }}
                                            >
                                                <div className="fw-medium">
                                                    {driver.name} {driver.surname}
                                                </div>
                                                <small className="text-muted">
                                                    ID: {driver.driver_id} | Phone: {driver.phone}
                                                </small>
                                                {driver.plate && <div className="text-muted">Plate: {driver.plate}</div>}
                                            </div>
                                        ))}
                                    </div>
                                    </>
                                )}
                            </Form.Group>

                            {selectedWithdrawalDriver && (
                                <Alert variant="warning" className="mb-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <FaUser className="me-2" />
                                            <div>
                                                <strong>Selected Driver:</strong><br />
                                                {selectedWithdrawalDriver.name} {selectedWithdrawalDriver.surname}
                                            </div>
                                        </div>
                                        {driverBalance && (
                                            <div className="text-end">
                                                <small className="text-muted">Available Balance</small><br />
                                                <span className="fw-bold text-success">${driverBalance.current_balance.toFixed(2)}</span>
                                            </div>
                                        )}
                                    </div>
                                </Alert>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaHandHoldingUsd className="me-1 text-warning" />
                                    Withdrawal Method
                                </Form.Label>
                                <Form.Select 
                                    value={withdrawalMethod} 
                                    onChange={(e) => setWithdrawalMethod(e.target.value)}
                                    className="mb-2"
                                >
                                    <option value="cash_pickup">Cash Pickup</option>
                                    <option value="bank_transfer">Bank Transfer</option>
                                    <option value="mobile_money">Mobile Money</option>
                                </Form.Select>
                            </Form.Group>

                            {withdrawalMethod === 'bank_transfer' && (
                                <div className="border rounded p-3 mb-3 bg-light">
                                    <h6 className="mb-3">Bank Transfer Details</h6>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Bank Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter bank name"
                                            value={withdrawalDetails.bank_name}
                                            onChange={(e) => setWithdrawalDetails({...withdrawalDetails, bank_name: e.target.value})}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Account Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter account number"
                                            value={withdrawalDetails.bank_account_number}
                                            onChange={(e) => setWithdrawalDetails({...withdrawalDetails, bank_account_number: e.target.value})}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Account Holder Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter account holder name"
                                            value={withdrawalDetails.account_holder_name}
                                            onChange={(e) => setWithdrawalDetails({...withdrawalDetails, account_holder_name: e.target.value})}
                                        />
                                    </Form.Group>
                                </div>
                            )}

                            {withdrawalMethod === 'mobile_money' && (
                                <div className="border rounded p-3 mb-3 bg-light">
                                    <h6 className="mb-3">Mobile Money Details</h6>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Provider</Form.Label>
                                        <Form.Select
                                            value={withdrawalDetails.mobile_money_provider}
                                            onChange={(e) => setWithdrawalDetails({...withdrawalDetails, mobile_money_provider: e.target.value})}
                                        >
                                            <option value="">Select provider...</option>
                                            <option value="ecocash">EcoCash</option>
                                            <option value="onemoney">OneMoney</option>
                                            <option value="telecash">TeleCash</option>
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Mobile Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter mobile money number"
                                            value={withdrawalDetails.mobile_money_number}
                                            onChange={(e) => setWithdrawalDetails({...withdrawalDetails, mobile_money_number: e.target.value})}
                                        />
                                    </Form.Group>
                                </div>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">
                                    <FaMoneyBillWave className="me-1 text-warning" />
                                    Withdrawal Amount ($)
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={withdrawalAmount}
                                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                                    size="lg"
                                />
                                <small className="text-muted" style={{fontSize: '14px'}}>
                                    Available balance: <span className="fw-bold text-success" style={{fontSize: '16px'}}>${driverBalance ? driverBalance.current_balance.toFixed(2) : '0.00'}</span>
                                    {driverBalance && parseFloat(withdrawalAmount) > driverBalance.current_balance && (
                                        <span className="text-warning ms-2">
                                            ⚠️ Will create negative balance (testing mode)
                                        </span>
                                    )}
                                </small>
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label className="fw-semibold">
                                    <FaFileAlt className="me-1 text-secondary" />
                                    Withdrawal Notes (Optional)
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Add notes about this driver earnings withdrawal..."
                                    value={withdrawalNotes}
                                    onChange={(e) => setWithdrawalNotes(e.target.value)}
                                />
                            </Form.Group>

                            <div className="d-grid gap-2">
                                <Button 
                                    variant="primary" 
                                    size="lg" 
                                    className="w-100"
                                    onClick={createWithdrawalRequest}
                                    disabled={!selectedWithdrawalDriver || !withdrawalAmount || !selectedTeller || isCreatingWithdrawalRequest}
                                >
                                    {isCreatingWithdrawalRequest ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                                            Creating Request...
                                        </>
                                    ) : (
                                        <>
                                            <FaPlus className="me-2" />
                                            Create Withdrawal Request
                                        </>
                                    )}
                                </Button>
                                
                                <small className="text-muted text-center d-block mb-2">
                                    OR process existing pending requests below
                                </small>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </div>

            {/* Summary Section */}
            <div className="col-lg-7">
                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <FaHandHoldingUsd className="text-warning mb-2" size={24} />
                                <h6 className="text-muted mb-1">Driver Withdrawals</h6>
                                <h4 className="text-warning mb-0">{driverFundsSummary?.total_withdrawals || 0}</h4>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <FaMoneyBillWave className="text-danger mb-2" size={24} />
                                <h6 className="text-muted mb-1">Total Withdrawn</h6>
                                <h4 className="text-danger mb-0">${(driverFundsSummary?.total_withdrawn || 0).toFixed(2)}</h4>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-4">
                        <Card className="text-center border-0 shadow-sm">
                            <Card.Body>
                                <FaChartLine className="text-info mb-2" size={24} />
                                <h6 className="text-muted mb-1">Pending Withdrawals</h6>
                                <h4 className="text-info mb-0">{driverFundsSummary?.pending_withdrawals || pendingWithdrawals.length}</h4>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Pending Withdrawals Section */}
                <Card className="shadow-sm border-0 mb-4">
                    <Card.Header className="bg-info text-white" onClick={() => toggleSection('pending-withdrawals')} style={{cursor: 'pointer'}}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="flex items-center space-x-3">
                                <FaChartLine size={20} />
                                <h3 className="text-lg font-semibold">Pending Driver Withdrawals</h3>
                                <span className="bg-white/20 px-2 py-1 rounded text-sm">
                                    {pendingWithdrawals.length} pending requests
                                </span>
                                {selectedTeller && (
                                    <span className="bg-green-500 px-2 py-1 rounded text-sm">
                                        Processing as: {selectedTeller}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fetchPendingWithdrawals();
                                    }}
                                    className="p-2 hover:bg-white/20 rounded transition-colors"
                                    title="Refresh pending withdrawals"
                                >
                                    <FaSync size={16} />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        exportData(pendingWithdrawals, 'pending_withdrawals.csv');
                                    }}
                                    className="p-2 hover:bg-white/20 rounded transition-colors"
                                    title="Export pending withdrawals"
                                >
                                    <FaDownload size={16} />
                                </button>
                                {expandedSections['pending-withdrawals'] ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                            </div>
                        </div>
                    </Card.Header>
                    
                    {expandedSections['pending-withdrawals'] && (
                        <div className="p-4">
                            {/* Quick Processing Instructions */}
                            {pendingWithdrawals.length > 0 && !selectedTeller && (
                                <Alert variant="warning" className="mb-3">
                                    <FaInfo className="me-2" />
                                    <strong>Quick Processing:</strong> Select a teller from the dropdown above to enable one-click processing of pending withdrawals.
                                </Alert>
                            )}
                            
                            {/* Debug info */}
                            {console.log('🔄 Rendering pending withdrawals section:', {
                                count: pendingWithdrawals.length,
                                expanded: expandedSections['pending-withdrawals'],
                                data: pendingWithdrawals
                            })}
                            
                            <div className="table-responsive" style={{maxHeight: '400px'}}>
                                <table className="table table-hover mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>Request ID</th>
                                            <th>Driver</th>
                                            <th>Amount</th>
                                            <th>Method</th>
                                            <th>Status</th>
                                            <th>Requested</th>
                                            <th>Available Balance</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingWithdrawals.length > 0 ? (
                                            pendingWithdrawals.map(withdrawal => (
                                                <tr key={withdrawal.request_id}>
                                                    <td>
                                                        <span className="badge bg-primary">{withdrawal.request_id}</span>
                                                    </td>
                                                    <td>
                                                        <div className="fw-medium">{withdrawal.client_profile_id}</div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold text-warning">${withdrawal.amount.toFixed(2)}</span>
                                                    </td>
                                                    <td>
                                                        <Badge variant="secondary" size="sm">
                                                            {withdrawal.withdrawal_method.replace('_', ' ')}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge variant="warning" size="sm">
                                                            {withdrawal.status}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {new Date(withdrawal.created_at).toLocaleString()}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold text-success">${withdrawal.available_balance.toFixed(2)}</span>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            onClick={() => quickProcessWithdrawal(withdrawal)}
                                                            disabled={!selectedTeller || quickProcessingWithdrawals.has(withdrawal.request_id)}
                                                            title={selectedTeller ? "Process this withdrawal" : "Select a teller first"}
                                                        >
                                                            {quickProcessingWithdrawals.has(withdrawal.request_id) ? (
                                                                <>
                                                                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                                                                    Processing...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FaHandHoldingUsd className="me-1" size={12} />
                                                                    Process
                                                                </>
                                                            )}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center text-muted py-4">
                                                    No pending withdrawal requests
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Recent Withdrawals */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                    <div 
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white cursor-pointer"
                        onClick={() => toggleSection('recent-withdrawals')}
                    >
                        <div className="flex items-center space-x-3">
                            <FaChartLine size={20} />
                            <h3 className="text-lg font-semibold">Recent Driver Withdrawals</h3>
                            <span className="bg-white/20 px-2 py-1 rounded text-sm">
                                {driverWithdrawals.length} records
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    exportData(driverWithdrawals, 'driver_withdrawals.csv');
                                }}
                                className="p-2 hover:bg-white/20 rounded transition-colors"
                            >
                                <FaDownload size={16} />
                            </button>
                            {expandedSections['recent-withdrawals'] ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                        </div>
                    </div>
                    
                    {expandedSections['recent-withdrawals'] && (
                        <div className="p-4">
                            <div className="table-responsive" style={{maxHeight: '400px'}}>
                                <table className="table table-hover mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>Time</th>
                                            <th>Driver</th>
                                            <th>Withdrawal Amount</th>
                                            <th>Method</th>
                                            <th>Status</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {driverWithdrawals.length > 0 ? (
                                            driverWithdrawals.slice(0, 10).map(transaction => (
                                                <tr key={transaction.transaction_id}>
                                                    <td>
                                                        <small className="text-muted">
                                                            {new Date(transaction.transaction_date).toLocaleTimeString()}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <div className="fw-medium">{transaction.customer_id || transaction.driver_id}</div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold text-danger">-${transaction.amount.toFixed(2)}</span>
                                                    </td>
                                                    <td>
                                                        <Badge variant="secondary" size="sm">
                                                            {transaction.payment_method}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge variant="success" size="sm">
                                                            {transaction.status}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {transaction.notes || '-'}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center text-muted py-4">
                                                    No driver withdrawals processed yet
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // Main component return
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-3">Loading Teller Control Panel...</span>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="row mb-4">
                <div className="col-12">
                    <h1 className="h3 mb-4">
                        <FaBalanceScale className="me-2 text-primary" />
                        Teller Control Panel
                    </h1>
                </div>
            </div>

            {/* Success and Error Modals */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{whiteSpace: 'pre-line'}}>{successMessage}</div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSuccessModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        {errorMessage}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Main Content */}
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
                <Tab eventKey="rental-deposits" title="Rental Deposits">
                    {renderRentalDepositsTab()}
                </Tab>
                <Tab eventKey="wallet-deposits" title="Wallet Deposits">
                    {renderWalletDepositsTab()}
                </Tab>
                <Tab eventKey="withdrawals" title="Driver Withdrawals">
                    {renderWithdrawalsTab()}
                </Tab>
            </Tabs>
        </div>
    );
};

export default TellerControlPanel;
